import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import Layout from '../../components/global/layout';
import loader from '../../methods/loader';
import { toast } from 'react-toastify';

// Global ConnectMeet (CM) preferences. Reused by the CM Scheduler to pre-fill the per-CM form
// and to resolve venues. Stored on users.meetingPreferences.

const ACTIVITIES = ['Breakfast', 'Coffee', 'Lunch', 'Other'];

// Shape returned by GET api/calendar/status
interface CalProviderState { connected: boolean; email?: string }
interface CalStatus {
    google: CalProviderState;
    microsoft: CalProviderState;
    googleConfigured?: boolean;
    microsoftConfigured?: boolean;
}

const CmPreferences: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
    const [loading, setLoading] = useState(true);
    const [businessAddress, setBusinessAddress] = useState('');
    const [addressOverride, setAddressOverride] = useState('');
    const [maxMiles, setMaxMiles] = useState<string>('');
    const [videoLink, setVideoLink] = useState('');
    // favoritePlaces keyed by activity -> [{name,address}]
    const [places, setPlaces] = useState<Record<string, { name: string; address: string }[]>>({});

    // Calendar free/busy connections
    const [searchParams, setSearchParams] = useSearchParams();
    const [calStatus, setCalStatus] = useState<CalStatus | null>(null);
    const [calConnecting, setCalConnecting] = useState('');

    const loadCalStatus = () => {
        ApiClient.get('api/calendar/status').then((res: any) => {
            if (res.success) setCalStatus(res.data);
        }).catch(() => { /* non-fatal */ });
    };

    // Handle the ?calendar=<provider>&status=<connected|error> return from the OAuth callback.
    useEffect(() => {
        const provider = searchParams.get('calendar');
        const status = searchParams.get('status');
        if (provider) {
            const nice = provider === 'google' ? 'Google Calendar' : 'Outlook Calendar';
            if (status === 'connected') toast.success(`${nice} connected`);
            else if (status === 'error') toast.error(`Could not connect ${nice}. Please try again.`);
            searchParams.delete('calendar');
            searchParams.delete('status');
            searchParams.delete('reason');
            setSearchParams(searchParams, { replace: true });
        }
        loadCalStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connectCalendar = (provider: 'google' | 'microsoft') => {
        setCalConnecting(provider);
        ApiClient.get(`api/calendar/${provider}/connect`).then((res: any) => {
            setCalConnecting('');
            if (res.success && res.url) {
                window.location.href = res.url; // same-tab, like the Zoom connect flow
            } else {
                toast.error(res.message || 'Could not start calendar connection');
            }
        }).catch(() => { setCalConnecting(''); toast.error('Could not start calendar connection'); });
    };

    const disconnectCalendar = (provider: 'google' | 'microsoft') => {
        loader(true);
        ApiClient.delete('api/calendar/disconnect', { provider }).then((res: any) => {
            loader(false);
            if (res.success) { toast.success('Disconnected'); loadCalStatus(); }
            else toast.error(res.message || 'Disconnect failed');
        }).catch(() => { loader(false); toast.error('Disconnect failed'); });
    };

    // cw: which of the Google account's calendars to read free/busy from (multi-calendar support).
    const [googleCals, setGoogleCals] = useState<{ id: string; summary: string; primary: boolean }[]>([]);
    const [googleCalSel, setGoogleCalSel] = useState<Set<string>>(new Set());
    const [calNeedsReconnect, setCalNeedsReconnect] = useState(false);
    const [calListLoading, setCalListLoading] = useState(false);
    const [calSaving, setCalSaving] = useState(false);

    const loadGoogleCalendars = () => {
        setCalListLoading(true);
        ApiClient.get('api/calendar/calendars', { provider: 'google' }).then((res: any) => {
            setCalListLoading(false);
            if (res.success) {
                setGoogleCals(res.data?.calendars || []);
                setGoogleCalSel(new Set<string>(res.data?.selectedCalendarIds || []));
                setCalNeedsReconnect(!!res.data?.needsReconnect);
            }
        }).catch(() => setCalListLoading(false));
    };

    // Load the Google calendar list whenever the Google account is connected.
    useEffect(() => {
        if (calStatus?.google?.connected) loadGoogleCalendars();
        else { setGoogleCals([]); setGoogleCalSel(new Set()); setCalNeedsReconnect(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calStatus?.google?.connected]);

    const toggleGoogleCal = (id: string) => {
        setGoogleCalSel(prev => {
            const next = new Set<string>(Array.from(prev));
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const saveGoogleCalendars = () => {
        setCalSaving(true);
        ApiClient.post('api/calendar/calendars/select', { provider: 'google', selectedCalendarIds: Array.from(googleCalSel) }).then((res: any) => {
            setCalSaving(false);
            if (res.success) toast.success('Calendar selection saved');
            else toast.error(res.message || 'Could not save calendar selection');
        }).catch(() => { setCalSaving(false); toast.error('Could not save calendar selection'); });
    };

    useEffect(() => {
        loader(true);
        ApiClient.get('api/cm/preferences').then((res: any) => {
            loader(false); setLoading(false);
            if (res.success) {
                const d = res.data;
                setBusinessAddress(d.businessAddress || '');
                setAddressOverride(d.businessAddressOverride || '');
                setMaxMiles(d.maxDrivingMiles != null ? String(d.maxDrivingMiles) : '');
                setVideoLink(d.preferredVideoLink || '');
                const map: Record<string, { name: string; address: string }[]> = {};
                for (const fp of (d.favoritePlaces || [])) {
                    map[fp.activity] = (fp.places || []).map((p: any) => ({ name: p.name || '', address: p.address || '' }));
                }
                setPlaces(map);
            }
        }).catch(() => { loader(false); setLoading(false); });
    }, []);

    const addPlace = (activity: string) => setPlaces(prev => ({ ...prev, [activity]: [...(prev[activity] || []), { name: '', address: '' }] }));
    const updatePlace = (activity: string, i: number, field: 'name' | 'address', val: string) =>
        setPlaces(prev => {
            const list = [...(prev[activity] || [])];
            list[i] = { ...list[i], [field]: val };
            return { ...prev, [activity]: list };
        });
    const removePlace = (activity: string, i: number) =>
        setPlaces(prev => ({ ...prev, [activity]: (prev[activity] || []).filter((_, j) => j !== i) }));

    const save = () => {
        const favoritePlaces = ACTIVITIES
            .filter(a => (places[a] || []).some(p => p.name.trim()))
            .map(a => ({ activity: a, places: (places[a] || []).filter(p => p.name.trim()) }));
        loader(true);
        ApiClient.post('api/cm/preferences/save', {
            favoritePlaces,
            maxDrivingMiles: maxMiles ? Number(maxMiles) : null,
            businessAddressOverride: addressOverride,
            preferredVideoLink: videoLink,
        }).then((res: any) => {
            loader(false);
            if (res.success) toast.success('Preferences saved'); else toast.error(res.message || 'Save failed');
        }).catch(() => { loader(false); toast.error('Save failed'); });
    };

    if (loading) {
        const loadingEl = <div className="p-6 text-gray-500">Loading…</div>;
        return embedded ? loadingEl : <Layout>{loadingEl}</Layout>;
    }

    const content = (
            <div className={embedded ? '' : 'p-6 max-w-3xl'}>
                {!embedded && (
                    <>
                        <h2 className="text-2xl font-bold mb-1">ConnectMeet Preferences</h2>
                        <p className="text-sm text-gray-500 mb-6">These defaults pre-fill your ConnectMeet scheduling forms.</p>
                    </>
                )}

                <div id="favorite-places" className="bg-white rounded-xl shadow p-6 mb-5 scroll-mt-20">
                    <h3 className="font-semibold mb-3">Favorite places by activity</h3>
                    {ACTIVITIES.map(a => (
                        <div key={a} className="mb-4">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700">{a}</span>
                                <button onClick={() => addPlace(a)} className="text-orange-500 text-sm">+ Add place</button>
                            </div>
                            {(places[a] || []).map((p, i) => (
                                <div key={i} className="flex gap-2 mb-2">
                                    <input value={p.name} onChange={e => updatePlace(a, i, 'name', e.target.value)} placeholder="Place name" className="flex-1 border rounded p-2 text-sm" />
                                    <input value={p.address} onChange={e => updatePlace(a, i, 'address', e.target.value)} placeholder="Address" className="flex-1 border rounded p-2 text-sm" />
                                    <button onClick={() => removePlace(a, i)} className="text-gray-400 hover:text-red-500 px-2">✕</button>
                                </div>
                            ))}
                            {!(places[a] || []).length && <p className="text-xs text-gray-400">No places yet.</p>}
                        </div>
                    ))}
                </div>

                <div id="calendar-connections" className="bg-white rounded-xl shadow p-6 mb-5 scroll-mt-20">
                    <h3 className="font-semibold mb-1">Calendar connections</h3>
                    <p className="text-sm text-gray-500 mb-3">
                        Connect a calendar so we can hide the times you're already busy when you pick your availability.
                    </p>

                    {/* Google */}
                    <div className="flex items-center justify-between border rounded-lg p-3 mb-2">
                        <div>
                            <div className="text-sm font-medium text-gray-800">Google Calendar</div>
                            {calStatus?.google?.connected
                                ? <div className="text-xs text-green-600">Connected{calStatus.google.email ? ` · ${calStatus.google.email}` : ''}</div>
                                : <div className="text-xs text-gray-400">Not connected</div>}
                        </div>
                        {calStatus?.google?.connected
                            ? <button onClick={() => disconnectCalendar('google')} className="text-sm text-gray-500 hover:text-red-500 border rounded px-3 py-1.5">Disconnect</button>
                            : <button onClick={() => connectCalendar('google')} disabled={calStatus?.googleConfigured === false || calConnecting === 'google'} className="text-sm bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded px-3 py-1.5">{calConnecting === 'google' ? 'Connecting…' : 'Connect'}</button>}
                    </div>

                    {/* Which Google calendars to read availability from (multi-calendar support) */}
                    {calStatus?.google?.connected && (
                        <div className="border rounded-lg p-3 mb-2 bg-gray-50">
                            {calNeedsReconnect ? (
                                <p className="text-xs text-amber-600">
                                    To choose which calendars to read, please Disconnect and Connect Google again — we now read your calendar list so you can pick.
                                </p>
                            ) : calListLoading ? (
                                <p className="text-xs text-gray-400">Loading your calendars…</p>
                            ) : googleCals.length ? (
                                <>
                                    <p className="text-xs font-medium text-gray-700 mb-2">Read my availability from these calendars:</p>
                                    <div className="space-y-1 mb-3">
                                        {googleCals.map(c => (
                                            <label key={c.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                <input type="checkbox" checked={googleCalSel.has(c.id)} onChange={() => toggleGoogleCal(c.id)} />
                                                <span>{c.summary}{c.primary ? ' (primary)' : ''}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <button onClick={saveGoogleCalendars} disabled={calSaving} className="text-sm bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded px-3 py-1.5">
                                        {calSaving ? 'Saving…' : 'Save calendars'}
                                    </button>
                                </>
                            ) : null}
                        </div>
                    )}

                    {/* Microsoft */}
                    <div className="flex items-center justify-between border rounded-lg p-3">
                        <div>
                            <div className="text-sm font-medium text-gray-800">Outlook Calendar</div>
                            {calStatus?.microsoft?.connected
                                ? <div className="text-xs text-green-600">Connected{calStatus.microsoft.email ? ` · ${calStatus.microsoft.email}` : ''}</div>
                                : <div className="text-xs text-gray-400">Not connected</div>}
                        </div>
                        {calStatus?.microsoft?.connected
                            ? <button onClick={() => disconnectCalendar('microsoft')} className="text-sm text-gray-500 hover:text-red-500 border rounded px-3 py-1.5">Disconnect</button>
                            : <button onClick={() => connectCalendar('microsoft')} disabled={calStatus?.microsoftConfigured === false || calConnecting === 'microsoft'} className="text-sm bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded px-3 py-1.5">{calConnecting === 'microsoft' ? 'Connecting…' : 'Connect'}</button>}
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow p-6 mb-5">
                    <h3 className="font-semibold mb-3">Travel & video</h3>
                    <label className="block text-sm text-gray-600 mb-1">Business address (from your profile)</label>
                    <input value={businessAddress} disabled className="w-full border rounded p-2 text-sm bg-gray-50 mb-3" />
                    <label className="block text-sm text-gray-600 mb-1">Override business address (optional)</label>
                    <input value={addressOverride} onChange={e => setAddressOverride(e.target.value)} className="w-full border rounded p-2 text-sm mb-3" />
                    <label className="block text-sm text-gray-600 mb-1">Maximum driving distance (miles)</label>
                    <input type="number" value={maxMiles} onChange={e => setMaxMiles(e.target.value)} className="w-40 border rounded p-2 text-sm mb-3" />
                    <label className="block text-sm text-gray-600 mb-1">Preferred video-conference link (virtual CMs)</label>
                    <input value={videoLink} onChange={e => setVideoLink(e.target.value)} placeholder="https://zoom.us/j/…" className="w-full border rounded p-2 text-sm" />
                </div>

                <div className="flex justify-end">
                    <button onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-all">Save preferences</button>
                </div>
            </div>
    );
    return embedded ? content : <Layout>{content}</Layout>;
};

export default CmPreferences;
